// Answer options added at runtime — /api/custom-answer.
//
// A size typed into the Flange to Nozzle End box that is not in the list gets created in
// NetSuite and recorded in cpq2_custom_answers. Two things then have to happen in the app,
// and BOTH are required:
//
//   1. The option is merged into the question, or it cannot be selected or even seen.
//   2. Its token is handed to the encoder, or selecting it encodes the slot's null token
//      and the size silently vanishes from the item.
//
// (2) is the dangerous half, because nothing errors: the mapping is still 367 valid
// characters, just missing the value.

import type { Question } from "../types";

const API_BASE = "/api";

export type CustomAnswer = {
  questionCode: string;
  answerCode: string;
  label: string;
  /** The NetSuite internal id, which IS the MAPPING token. */
  token: string;
};

/** The global the MAPPING encoder reads its extra tokens from. */
type TokenCarrier = { __CPQ_EXTRA_TOKENS?: Record<string, Record<string, string>> };

/** Read every runtime-added option for a product. NEVER throws: a configurator that cannot
 *  reach this API must still open, just without the extra options. */
export async function fetchCustomAnswers(product: string): Promise<CustomAnswer[]> {
  try {
    const res = await fetch(`${API_BASE}/custom-answer?product=${encodeURIComponent(product)}`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      console.warn(`[cpq2] custom-answer GET ${res.status} — continuing without runtime options`);
      return [];
    }
    const body = (await res.json()) as { ok?: boolean; answers?: CustomAnswer[] };
    if (!body || body.ok !== true || !Array.isArray(body.answers)) return [];
    return body.answers.filter((a) => a && a.questionCode && a.answerCode && a.label && a.token);
  } catch (e) {
    console.warn("[cpq2] custom-answer GET failed — continuing without runtime options", e);
    return [];
  }
}

/** Create one. Unlike the read, a failure here MUST surface: the user typed a size and needs
 *  to know whether it took. */
export async function createCustomAnswer(args: {
  product: string;
  questionCode: string;
  label: string;
  userId?: string;
  userName?: string;
}): Promise<{ ok: true; answer: CustomAnswer; created: boolean } | { ok: false; reason: string }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/custom-answer`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(args),
    });
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : String(e) };
  }
  type CreateReply = {
    ok?: boolean;
    reason?: string;
    created?: boolean;
    answerCode?: string;
    label?: string;
    token?: string;
  };
  let body: CreateReply | null = null;
  try {
    body = (await res.json()) as CreateReply;
  } catch {
    body = null;
  }
  if (!res.ok || !body || body.ok !== true || !body.answerCode || !body.token) {
    return { ok: false, reason: (body && body.reason) || `HTTP ${res.status}` };
  }
  return {
    ok: true,
    created: body.created === true,
    answer: {
      questionCode: args.questionCode,
      answerCode: body.answerCode,
      label: body.label || args.label,
      token: body.token,
    },
  };
}

/** Publish tokens to the encoder. Merges rather than replaces, so a value added mid-session
 *  does not drop the ones loaded at start. */
export function registerTokens(answers: CustomAnswer[]): void {
  if (answers.length === 0) return;
  const g = globalThis as unknown as TokenCarrier;
  const map: Record<string, Record<string, string>> = g.__CPQ_EXTRA_TOKENS ?? {};
  for (const a of answers) {
    if (!map[a.questionCode]) map[a.questionCode] = {};
    map[a.questionCode][a.answerCode] = String(a.token);
  }
  g.__CPQ_EXTRA_TOKENS = map;
}

/** Numeric size of a label, for ordering: `19-3/4"` -> 19.75. NaN when it is not a size. */
function sizeOf(label: string): number {
  const m = /^(\d+)(?:-(\d+)\/(\d+))?"?$/.exec(String(label).trim());
  if (!m) return NaN;
  return Number(m[1]) + (m[2] && Number(m[3]) ? Number(m[2]) / Number(m[3]) : 0);
}

/** Add the options to their questions, IN PLACE, cloning a sibling so every one of the ~75
 *  answer fields is present. Skips anything already there, so it is safe to call twice.
 *  Returns how many were added. */
export function mergeCustomAnswers(questions: Question[], answers: CustomAnswer[]): number {
  if (answers.length === 0) return 0;
  let added = 0;
  for (const a of answers) {
    const q = questions.find((x) => x.custrecord_cpqc_question_code === a.questionCode);
    if (!q || !Array.isArray(q._answers) || q._answers.length === 0) continue;
    const existing = q._answers.find((x) => x.custrecord_cpqc_answer_code === a.answerCode);
    if (existing) {
      // Already an option, so nothing to add - but being recorded is exactly what LISTS a
      // size that was previously typeable-only. This is how using one puts it in everyone's
      // dropdown from then on.
      if (existing._hiddenUntilUsed) {
        delete existing._hiddenUntilUsed;
        added++;
      }
      continue;
    }

    // Clone the LAST answer by sequence: for a size list that is the largest, so the new
    // one inherits a sibling of the same kind rather than a "None"-style outlier.
    const template = [...q._answers].sort(
      (x, y) => Number(x.custrecord_cpqc_answer_sequence || 0) - Number(y.custrecord_cpqc_answer_sequence || 0)
    )[q._answers.length - 1];
    const clone = JSON.parse(JSON.stringify(template)) as (typeof q._answers)[number];
    clone.id = `custom-${a.questionCode}-${a.answerCode}`;
    clone.custrecord_cpqc_answer_code = a.answerCode;
    clone.custrecord_cpqc_answer_label_html = a.label;
    clone.name = a.label;
    // Sequence follows the question's own scheme (size x 10), so a new size sorts into
    // place instead of landing at the end. Falls back to after the last one.
    const size = sizeOf(a.label);
    const lastSeq = Number(template.custrecord_cpqc_answer_sequence || 0);
    clone.custrecord_cpqc_answer_sequence = String(Number.isFinite(size) ? size * 10 : lastSeq + 1);
    // A clone must not inherit the template's rules.
    for (const k of Object.keys(clone) as Array<keyof typeof clone>) {
      if (/hide_rule|ronly_rule|_rule$/.test(String(k))) (clone as Record<string, unknown>)[String(k)] = "";
    }
    // Nor its listing state. The template is the LAST option by sequence, which for Flange
    // to Nozzle End is one of the sizes marked _hiddenUntilUsed - so without this a size
    // someone just created would be added to the dropdown already hidden from it.
    delete clone._hiddenUntilUsed;
    q._answers.push(clone);
    added++;
  }
  return added;
}

/** Load, merge and register in one step. Called once per configurator load. */
export async function applyCustomAnswers(product: string, questions: Question[]): Promise<CustomAnswer[]> {
  const answers = await fetchCustomAnswers(product);
  if (answers.length === 0) return [];
  mergeCustomAnswers(questions, answers);
  registerTokens(answers);
  return answers;
}
